import { useEffect, useMemo, useState } from "react";
import { Blocks as BlocksIcon, Loader2, Plus } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useWorkspace } from "@/hooks/useWorkspace";
import { toast } from "@/hooks/use-toast";
import {
  BLOCK_TYPE_ORDER,
  blocksByType,
  countBlockUsageInBlueprints,
  createBlock,
  deleteBlock,
  ensureSeedBlocks,
  getCategoryOrder,
  listBlocks,
  restoreMissingSeedBlocks,
  saveCategoryOrder,
  updateBlock,
  type ComposerBlock,
  type ComposerBlockImageAlign,
  type ComposerBlockImagePosition,
  type ComposerBlockImageSize,
  type ComposerBlockType,
} from "@/lib/composerBlocks";
import { BlockCategoryAccordion } from "./BlockCategoryAccordion";
import { BlockEditorDialog } from "./BlockEditorDialog";

type BlocksPanelProps = {
  /** "catalog" — Builder-tab CRUD; "drag" — compact right-rail draggable rows. */
  mode?: "catalog" | "drag";
};

const blocksQueryKey = (workspaceId: string | undefined) => ["composer-blocks", workspaceId] as const;

export const BlocksPanel = ({ mode = "catalog" }: BlocksPanelProps) => {
  const { active } = useWorkspace();
  const workspaceId = active?.id;
  const qc = useQueryClient();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ComposerBlock | null>(null);
  const [draftType, setDraftType] = useState<ComposerBlockType>("global");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [draftSignatureName, setDraftSignatureName] = useState("");
  const [draftImageUrl, setDraftImageUrl] = useState<string | null>(null);
  const [draftImagePath, setDraftImagePath] = useState<string | null>(null);
  const [draftImageAlt, setDraftImageAlt] = useState<string>("");
  const [draftImagePosition, setDraftImagePosition] = useState<ComposerBlockImagePosition>("below");
  const [draftImageSize, setDraftImageSize] = useState<ComposerBlockImageSize>("medium");
  const [draftImageAlign, setDraftImageAlign] = useState<ComposerBlockImageAlign>("left");
  const [altError, setAltError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ComposerBlock | null>(null);

  const { data: blocks = [], isLoading } = useQuery({
    queryKey: blocksQueryKey(workspaceId),
    enabled: !!workspaceId,
    queryFn: async () => {
      if (!workspaceId) return [];
      try {
        await ensureSeedBlocks(workspaceId);
      } catch {
        // non-fatal — list still works against whatever exists
      }
      return listBlocks(workspaceId);
    },
  });

  const categoryOrderQueryKey = ["composer-block-category-order", workspaceId] as const;
  const { data: categoryOrder = BLOCK_TYPE_ORDER } = useQuery({
    queryKey: categoryOrderQueryKey,
    enabled: !!workspaceId,
    queryFn: () => getCategoryOrder(workspaceId!),
  });

  const { data: usageCounts } = useQuery({
    queryKey: ["composer-block-usage", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => countBlockUsageInBlueprints(workspaceId!),
  });

  const [dragType, setDragType] = useState<ComposerBlockType | null>(null);
  const [dragOverType, setDragOverType] = useState<ComposerBlockType | null>(null);

  const onCategoryDrop = async (target: ComposerBlockType) => {
    const source = dragType;
    setDragType(null);
    setDragOverType(null);
    if (!workspaceId || !source || source === target) return;
    const next = categoryOrder.filter((t) => t !== source);
    const idx = next.indexOf(target);
    next.splice(idx, 0, source);
    const prev = categoryOrder;
    qc.setQueryData(categoryOrderQueryKey, next);
    try {
      await saveCategoryOrder(workspaceId, next);
    } catch (e) {
      qc.setQueryData(categoryOrderQueryKey, prev);
      toast({ title: "Could not save category order", description: (e as Error).message, variant: "destructive" });
    }
  };

  const grouped = useMemo(() => blocksByType(blocks), [blocks]);

  const openNew = (type?: ComposerBlockType) => {
    setEditing(null);
    setDraftType(type ?? "global");
    setDraftTitle("");
    setDraftBody("");
    setDraftSignatureName("");
    setDraftImageUrl(null);
    setDraftImagePath(null);
    setDraftImageAlt("");
    setDraftImagePosition("below");
    setDraftImageSize("medium");
    setDraftImageAlign("left");
    setAltError(false);
    setEditorOpen(true);
  };

  const openEdit = (block: ComposerBlock) => {
    setEditing(block);
    setDraftType(block.type);
    setDraftTitle(block.title);
    setDraftBody(block.body);
    setDraftSignatureName(block.signatureName ?? "");
    setDraftImageUrl(block.imageUrl ?? null);
    setDraftImagePath(block.imagePath ?? null);
    setDraftImageAlt(block.imageAlt ?? "");
    setDraftImagePosition((block.imagePosition ?? "below") as ComposerBlockImagePosition);
    setDraftImageSize((block.imageSize ?? "medium") as ComposerBlockImageSize);
    setDraftImageAlign((block.imageAlign ?? "left") as ComposerBlockImageAlign);
    setAltError(false);
    setEditorOpen(true);
  };

  const onSave = async () => {
    if (!workspaceId) return;
    const title = draftTitle.trim();
    if (!title) {
      toast({ title: "Title required", variant: "destructive" });
      return;
    }
    const typeAllowsImage = draftType === "signature" || draftType === "author_bio" || draftType === "product_cta";
    if (typeAllowsImage && draftImageUrl && !draftImageAlt.trim()) {
      setAltError(true);
      toast({ title: "Alt text required", description: "Add alt text for the image (used by screen readers).", variant: "destructive" });
      return;
    }
    setAltError(false);
    setSaving(true);
    try {
      const imageFields = typeAllowsImage
        ? {
            imageUrl: draftImageUrl,
            imagePath: draftImagePath,
            imageAlt: draftImageAlt.trim(),
            imagePosition: draftImagePosition,
            imageSize: draftImageSize,
            imageAlign: draftImageAlign,
          }
        : { imageUrl: null, imagePath: null, imageAlt: null, imagePosition: null, imageSize: null, imageAlign: null };

      const signatureName = draftType === "signature" ? draftSignatureName.trim() || null : null;
      if (editing) {
        await updateBlock(editing.id, { title, body: draftBody, type: draftType, signatureName, ...imageFields });
      } else {
        await createBlock(workspaceId, { type: draftType, title, body: draftBody, signatureName, ...imageFields });
      }
      await qc.invalidateQueries({ queryKey: blocksQueryKey(workspaceId) });
      setEditorOpen(false);
    } catch (e) {
      toast({ title: "Could not save block", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!confirmDelete || !workspaceId) return;
    try {
      await deleteBlock(confirmDelete.id);
      await qc.invalidateQueries({ queryKey: blocksQueryKey(workspaceId) });
    } catch (e) {
      toast({ title: "Could not delete block", description: (e as Error).message, variant: "destructive" });
    } finally {
      setConfirmDelete(null);
    }
  };

  useEffect(() => {
    if (!editorOpen) return;
    // no-op; reserved for future per-type defaults
  }, [editorOpen]);

  if (!workspaceId) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-xs text-muted-foreground">
        No active workspace.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      {mode === "catalog" && (
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <BlocksIcon className="mt-0.5 h-4 w-4 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Reusable Blocks</p>
              <p className="text-xs text-muted-foreground">
                Create and edit your own blocks. Drag any block from the right-rail Blocks tab into a draft.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              title="Re-insert any default blocks you've deleted. Existing blocks are not changed."
              onClick={async () => {
                if (!workspaceId) return;
                if (!confirm("Restore default blocks that are missing from this workspace? Your existing blocks will not be changed.")) return;
                try {
                  const n = await restoreMissingSeedBlocks(workspaceId);
                  await qc.invalidateQueries({ queryKey: blocksQueryKey(workspaceId) });
                  toast({ title: n === 0 ? "Nothing to restore" : `Restored ${n} default block${n === 1 ? "" : "s"}` });
                } catch (e) {
                  toast({ title: "Restore failed", description: (e as Error).message, variant: "destructive" });
                }
              }}
            >
              Restore defaults
            </Button>
            <Button size="sm" variant="outline" onClick={() => openNew()}>
              <Plus className="h-3.5 w-3.5" />
              New block
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">
          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Loading blocks…
        </div>
      ) : (
        <BlockCategoryAccordion
          mode={mode}
          categoryOrder={categoryOrder}
          grouped={grouped}
          usageCounts={usageCounts}
          dragType={dragType}
          setDragType={setDragType}
          dragOverType={dragOverType}
          setDragOverType={setDragOverType}
          onCategoryDrop={onCategoryDrop}
          openEdit={openEdit}
          openNew={openNew}
          setConfirmDelete={setConfirmDelete}
        />
      )}

      <BlockEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        editing={editing}
        workspaceId={workspaceId}
        saving={saving}
        draftType={draftType}
        setDraftType={setDraftType}
        draftTitle={draftTitle}
        setDraftTitle={setDraftTitle}
        draftBody={draftBody}
        setDraftBody={setDraftBody}
        draftSignatureName={draftSignatureName}
        setDraftSignatureName={setDraftSignatureName}
        draftImageUrl={draftImageUrl}
        draftImagePath={draftImagePath}
        draftImageAlt={draftImageAlt}
        draftImagePosition={draftImagePosition}
        draftImageSize={draftImageSize}
        draftImageAlign={draftImageAlign}
        altError={altError}
        setAltError={setAltError}
        setDraftImageUrl={setDraftImageUrl}
        setDraftImagePath={setDraftImagePath}
        setDraftImageAlt={setDraftImageAlt}
        setDraftImagePosition={setDraftImagePosition}
        setDraftImageSize={setDraftImageSize}
        setDraftImageAlign={setDraftImageAlign}
        onSave={onSave}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this block?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.title}" will be permanently removed from this workspace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BlocksPanel;
